import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
    Box, Divider, Grid, IconButton, InputAdornment, Paper, Stack,
    Table, TableBody, TableContainer, TextField, Typography
} from '@mui/material'
import OrderIcon from '../../assets/icons/Order'
import { useDispatch, useSelector } from 'react-redux'
import { Security } from '../../proto/generated/security'
import websocketActions from '../../actions/websocket.actions'
import { subscriptionTags } from '../../assets/constants'
import { WebSocketContext } from '../webSocketConnection/WebSocket'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { selectOrdersHistory } from '../../selectors/orders'
import { RootState } from '../../store'
import Loading from '../common/Loading'
import { WidgetListItems } from '../../util/sidebar'
import { selectRightSidebarWidget } from '../../selectors/common'
import { selectQuickWidgetConfig } from '../../selectors/quickWidgets'
import { QuickWidgetTypes } from '../../reducer/user.reducer'
import ReconnectView from '../common/ReconnectView'
import ReconnectTradingAccount from '../common/ReconnectTradingAccount'
import TradingAccountSelector from '../tradingAccountStatus/TradingAccountSelector'
import { selectBrokers, selectPrimaryTradingAccount } from '../../selectors/user'
import { Order, OrderStatus } from '../../proto/generated/order'
import { Broker, TradingAccountStatus, TradingAccountSyncRequest, TradingAccountSyncRequest_Type } from '../../proto/generated/trading-bridge'
import { openConnectAccountDialog } from '../../reducer/orders.reducer'
import SearchIcon from '../../assets/icons/SearchIcon'
import RefreshIcon from '../../assets/icons/RefreshIcon'
import ClearIcon from '../../assets/icons/ClearIcon'
import { BrokerId } from '../../util/tradingAccounts'
import { syncOrders } from '../../services/apis/orders'
import EmptyView from './EmptyView'
import OrdersTableHeader from '../ordersWidget/OrdersTableHeader'
import OrderRow from '../ordersWidget/OrderRow'
import GenericFilterTabs from '../genericTabs/genericFilterTabs'
import PositionsSidebarWidget from '../positionsSidebarWidget/positionsSidebarWidget'
import { selectPositions } from '../../selectors/positions'
import { fetchSecuritySnapshotsBulk } from '../../services/apis/candles'
import { filterOrdersOpenTabWide, groupIcebergBasketOrders, isIcebergGroup } from '../../util/icebergOrders'
import OrderBatchGroupRow from '../ordersWidget/OrderBatchGroupRow'

export enum PositionNavButtons {
    PNL = 'PNL',
    GREEKS = 'GREEKS'
}

export const navItems = [
    {
        key: PositionNavButtons.PNL,
        label: 'P/L'

    },
    {
        key: PositionNavButtons.GREEKS,
        label: 'Greeks'
    }
]

export enum OrdersNavButtons {
    OPEN = 'OPEN',
    ALL = 'ALL'
}

export enum OrdersNavButtonsDetailed {
    OPEN = 'OPEN',
    COMPLETE = 'COMPLETE',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED',
    ALL = 'ALL',
}

export const positionsNavItems = [
    {
        key: OrdersNavButtons.OPEN,
        label: 'Open'
    },
    {
        key: OrdersNavButtons.ALL,
        label: 'All'
    }
]

export const ordersNavItems = [
    {
        key: OrdersNavButtonsDetailed.OPEN,
        label: 'Open'
    },
    {
        key: OrdersNavButtonsDetailed.COMPLETE,
        label: 'Complete'
    },
    {
        key: OrdersNavButtonsDetailed.REJECTED,
        label: 'Rejected'
    },
    {
        key: OrdersNavButtonsDetailed.CANCELLED,
        label: 'Cancelled'
    },
    {
        key: OrdersNavButtonsDetailed.ALL,
        label: 'All'
    },
]

export interface SelectedSqOffStrategyType {
    underlyingSecurity: Security | undefined
    strategyCode: string | undefined
    basketId: string | undefined
    strategyName: string | undefined
    index: number,
    analyzeAndAdjust?: boolean
}

const PositionsWidget = () => {
    const dispatch = useDispatch()
    const ws = useContext(WebSocketContext)
    const [showContent, setShowContent] = useState(false)
    const [showReconnect, setShowReconnect] = useState(false);
    const [tableViewType, setTableViewType] = useState<PositionNavButtons>(PositionNavButtons.PNL)
    const isDragging = useRef(false)
    const [ordersTableViewType, setOrdersTableViewType] = useState<OrdersNavButtonsDetailed>(OrdersNavButtonsDetailed.OPEN)
    const [searchText, setSearchText] = useState('')
    const [showSearch, setShowSearch] = useState(false)
    const [filterOrdersData, setFilterOrdersData] = useState<Order[]>([])
    const [positionsHeight, setPositionsHeight] = useState(0.6)

    const positions = useSelector(selectPositions)

    const brokers: Broker[] = useSelector(selectBrokers)
    const ordersData = useSelector(selectOrdersHistory)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const loading = useSelector((state: RootState) => state.positionsStore.loading)
    const rightSideWidget = useSelector(selectRightSidebarWidget)
    const positionWidgetConfig = useSelector(selectQuickWidgetConfig(QuickWidgetTypes.POSITION));

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (primaryTradingAccount) {
            setShowReconnect(false);
            setShowContent(true);
        } else {
            setShowContent(false);
            timer = setTimeout(() => {
                setShowReconnect(true);
            }, 200);
        }
        return () => {
            clearTimeout(timer);
            const isQuickWidgetOpen = positionWidgetConfig?.enabled === true;
            const isSidebarOpen = rightSideWidget === WidgetListItems.POSITIONS;
            if (!isQuickWidgetOpen && !isSidebarOpen) {
                websocketActions.setWebsocketSubscribeList(dispatch, {
                    securitySnapshotSubscriptions: [
                        {
                            tag: subscriptionTags.POSITION_WIDGET_SUBSCRIPTION_TAG,
                            securities: []
                        }
                    ],
                    candleSubscriptions: [
                        {
                            tag: subscriptionTags.POSITION_WIDGET_SUBSCRIPTION_TAG,
                            timeBucket: undefined,
                            securities: []
                        }
                    ]
                });
            }
        };
    }, [primaryTradingAccount]);


    const getFilteredOrders = (orders: Order[]) => {
        if (showSearch && searchText.trim().length > 0) {
            return orders;
        }
        switch (ordersTableViewType) {
            case OrdersNavButtonsDetailed.OPEN:
                return filterOrdersOpenTabWide(orders);
            case OrdersNavButtonsDetailed.COMPLETE:
                return orders.filter(order => order.status === OrderStatus.EXECUTED);
            case OrdersNavButtonsDetailed.REJECTED:
                return orders.filter(order => (order.status === OrderStatus.REJECTED || order.status === OrderStatus.FAILED));
            case OrdersNavButtonsDetailed.CANCELLED:
                return orders.filter(order => order.status === OrderStatus.CANCELLED);
            case OrdersNavButtonsDetailed.ALL:
                return orders;
            default:
                return orders;
        }
    };

    const visibleOrders = getFilteredOrders(filterOrdersData);
    
    // Loading snapshots for orders in right sidebar widget
    const securities = useMemo(()=>{
        return visibleOrders.filter(order => ![OrderStatus.REJECTED, OrderStatus.CANCELLED, OrderStatus.EXECUTED, OrderStatus.FAILED].includes(order.status)).map((order)=> order.security)
    },[visibleOrders])

    useEffect(() => {
        if (!securities || !securities.length) return;
        fetchSecuritySnapshotsBulk({ securities, dispatch });
        websocketActions.setWebsocketSubscribeList(dispatch, {
            securitySnapshotSubscriptions: [
                {
                    tag: subscriptionTags.ORDERS_WIDGET_SUBSCRIPTION_TAG,
                    securities
                }
            ]
        })
    }, [securities])

    const orderDataSecKeys = useMemo(() => {
        const data: string[] = []
        ordersData?.forEach((order) => {
            const secKey = getSecurityKey(order.security?.exchange ?? 0, order.security?.code ?? '')
            data.push(secKey)
        })
        return data
    }, [ordersData])
    useEffect(() => {
        let cancelled = false
        const run = async () => {
            if (!ordersData?.length) { setFilterOrdersData([]); return }
            const results = await Promise.all(
                ordersData.map((data, i) =>
                    getSecurityAsync(orderDataSecKeys[i]).then((security) =>
                        (security?.symbol)?.toLowerCase()?.includes(searchText.trim().toLowerCase())
                    )
                )
            )
            if (!cancelled) setFilterOrdersData(ordersData.filter((_, i) => results[i]))
        }
        run()
        return () => { cancelled = true }
    }, [orderDataSecKeys, ordersData, searchText])

    const handleOnSync = useCallback(async () => {
        if (primaryTradingAccount && primaryTradingAccount.status !== TradingAccountStatus.ACTIVE) {
            dispatch(openConnectAccountDialog({
                type: 'reconnect',
                tradingAccountId: primaryTradingAccount.accountId
            }))
        } else {
            if (primaryTradingAccount) {
                const body: TradingAccountSyncRequest = {
                    tradingAccountId: primaryTradingAccount.accountId,
                    types: [TradingAccountSyncRequest_Type.ORDER_SYNC],
                    force: true
                }
                syncOrders({ body }).then((response) => {
                    setSearchText('')
                }).catch((error) => {
                    console.error('Something went wrong in syncing orders')
                })
            }
        }
    }, [dispatch, primaryTradingAccount])

    const startDragging = () => {
        isDragging.current = true;
        document.body.style.userSelect = "none";
        document.body.style.pointerEvents = "none";
        document.body.style.cursor = "ns-resize";
    };

    const stopDragging = () => {
        isDragging.current = false;
        document.body.style.userSelect = "auto";
        document.body.style.pointerEvents = "auto";
        document.body.style.cursor = "auto";
    };

    const onDrag = (e: MouseEvent) => {
        if (!isDragging.current) return;
        document.body.style.cursor = "ns-resize";
        const containerElement = document.querySelector('[data-positions-container]') as HTMLElement;
        if (containerElement) {
            const rect = containerElement.getBoundingClientRect();
            const relativeY = e.clientY - rect.top;
            const percentage = relativeY / rect.height;
            const clampedPercentage = Math.max(0.20, Math.min(0.80, percentage));
            setPositionsHeight(clampedPercentage);
        }
    };

    useEffect(() => {
        window.addEventListener("mousemove", onDrag);
        window.addEventListener("mouseup", stopDragging);
        return () => {
            window.removeEventListener("mousemove", onDrag);
            window.removeEventListener("mouseup", stopDragging);
        };
    }, [])

    return (
        <Paper
            sx={{
                borderTopRightRadius: '6px',
                display: 'flex',
                height: '100%',
                flexDirection: 'column'
            }}
        >
            {
                loading ? <Loading size={30} /> : (showContent ? (
                    <>
                        <Box sx={{ display: 'flex', height: '100%', flexDirection: 'column' }} data-positions-container>
                            <Box sx={{
                                flex: positionsHeight,
                                minHeight: '200px',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden'
                            }}>
                                <PositionsSidebarWidget positions={positions} />
                            </Box>
                            <Box
                                className="divider"
                                sx={{
                                    height: 'var(--extra-small-spacing)',
                                    cursor: 'ns-resize',
                                    backgroundColor: (theme) => theme.palette.background.default,
                                }}
                                onMouseDown={startDragging}
                            />
                            <Box sx={{ flex: 1 - positionsHeight, overflow: 'auto', display: 'flex', flexDirection: 'column', minHeight: '200px' }}>
                                <Box sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                    <Grid container px={'8px'} py={0}
                                        justifyContent={'space-between'}
                                        alignItems={'center'} >
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <OrderIcon sx={{ color: (theme) => theme.palette.text.secondary }} />
                                            <Typography variant={'body1'}>Orders</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {
                                                brokers.find((broker) => broker.code === (primaryTradingAccount?.brokerCode ?? BrokerId.BROKER_UNKNOWN))?.syncAvailable &&
                                                <IconButton onClick={handleOnSync} sx={{ height: '40px' }}>
                                                    <RefreshIcon />
                                                </IconButton>
                                            }
                                            <TradingAccountSelector compact />
                                        </Box>
                                    </Grid>
                                    <Divider style={{ width: '100%' }} />
                                    <Grid container justifyContent={'space-between'} style={{ padding: '10px' }}>
                                        <Grid item>
                                            {!showSearch && (
                                                <GenericFilterTabs<OrdersNavButtonsDetailed>
                                                    items={ordersNavItems}
                                                    onChange={(val) => {
                                                        setOrdersTableViewType(val);
                                                    }}
                                                    value={ordersTableViewType}
                                                    tabProps={{ sx: { minWidth: 60 } }}
                                                    sx={{ height: 28, pr: 1 }}
                                                />)}
                                        </Grid>
                                        {!!(ordersData && ordersData?.length > 0) && (
                                            (!showSearch ? (
                                                <Grid item >
                                                    <IconButton onClick={() => setShowSearch(true)} size="small">
                                                        <SearchIcon />
                                                    </IconButton>
                                                </Grid>
                                            ) : (
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        autoFocus
                                                        value={searchText}
                                                        onChange={(e) => setSearchText(e.target.value)}
                                                        variant="outlined"
                                                        size="small"
                                                        placeholder="Search Symbol"
                                                        InputProps={{
                                                            sx: { height: 32, pl: 1 },
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => {
                                                                            setSearchText('');
                                                                            setShowSearch(false);
                                                                        }}
                                                                    >
                                                                        <ClearIcon />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                            )))
                                        }
                                    </Grid>
                                </Box>
                                {visibleOrders.length === 0 ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 0 }}>
                                        <EmptyView text={
                                            searchText.trim().length > 0
                                                ? "No orders found for your search."
                                                : "Your placed and executed orders will appear here."
                                        } />
                                    </Box>
                                ) : (
                                    <TableContainer>
                                        <Table>
                                            <OrdersTableHeader tabType={ordersTableViewType} />
                                            <TableBody>
                                                {(() => {
                                                    const grouped = groupIcebergBasketOrders(visibleOrders);
                                                    return grouped
                                                        .sort((a, b) => {
                                                            // For iceberg groups, use first order for sorting
                                                            const aOrder = isIcebergGroup(a) ? a.allOrders[0] : a;
                                                            const bOrder = isIcebergGroup(b) ? b.allOrders[0] : b;
                                                            return (bOrder?.created ?? 0) - (aOrder?.created ?? 0);
                                                        })
                                                        .map((orderOrGroup) => {
                                                            const key = isIcebergGroup(orderOrGroup) 
                                                                ? `iceberg-${orderOrGroup.batchId}-${orderOrGroup.basketId}` 
                                                                : orderOrGroup.clientOrderId;
                                                            
                                                            // Expandable basket batch (iceberg ladder or strategy-only)
                                                            if (isIcebergGroup(orderOrGroup) && orderOrGroup.isBasketGroup) {
                                                                return <OrderBatchGroupRow key={key} batchGroup={orderOrGroup} tabType={ordersTableViewType} hideMetaData={false} />;
                                                            }
                                                            
                                                            // Render iceberg groups (non-basket) and regular orders with OrderRow
                                                            return <OrderRow key={key} order={orderOrGroup} tabType={ordersTableViewType} />;
                                                        });
                                                })()}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Box>
                        </Box>
                    </>) : (
                    showReconnect && (<>
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Stack justifyContent={'center'} alignItems={'center'}>
                                <ReconnectView text={primaryTradingAccount ? "Reconnect trading account to view positions." : "Connect trading account to view positions."} />
                                <ReconnectTradingAccount primaryTradingAccount={primaryTradingAccount} />
                            </Stack>
                        </Box>
                    </>)
                )
                )
            }
        </Paper>
    )
}
export default memo(PositionsWidget);
